import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';

import { resetAddress } from '@r1-frontend/entities/ContactAddress/actions/resetAddress';
import { EVerificationStatus } from '@r1-frontend/entities/ContactAddress/initialState';

import Alert from '@r1-frontend/ui-react/components/Alert';
import { btnTypes } from '@r1-frontend/ui-react/components/buttons/baseButton';
import InfoCircle from '@r1-frontend/ui-react/components/svg/main/InfoCircle';

import EditableContactsForm from '~/src/components/CheckAddressMapConnect/EditableContactsForm';
import { ADDRESS_PARAM_NAME } from '~/src/features/full-buy/consts';
import { getFullBuyRoute } from '~/src/features/full-buy/helpers';
import { ETariffFilters } from '~/src/features/full-buy/store/initialState';
import { useAnalyticsSender } from '~/src/helpers/hooks/useAnaliticsSender';
import { setSessionStorageByKey } from '~/src/helpers/storage';
import { TState } from '~/src/store';

import {
    additionalBtnText,
    alertText,
    EConnectStatuses,
    texts,
} from './constants';

import * as ST from './styled';

interface IProps {
    connectStatus: EConnectStatuses,
}

export const ResultForm = (
    {
        connectStatus,
    }: IProps,
): JSX.Element => {
    const router = useRouter();
    const dispatch = useDispatch();
    const sendAnalytics = useAnalyticsSender('address verification');

    const { verifiedAddressData } = useSelector((state: TState) => ({
        verifiedAddressData: state.contactAddress.verifiedAddressData,
    }));

    const saveVerifiedAddress = useCallback(() => {
        if (verifiedAddressData.status === EVerificationStatus.verified) {
            setSessionStorageByKey(ADDRESS_PARAM_NAME, verifiedAddressData);
        }
    }, [verifiedAddressData]);

    const onClickChangeAddress = useCallback(() => {
        dispatch(resetAddress());
    }, [dispatch]);

    const onExecuteAction = () => {
        if (connectStatus === EConnectStatuses.NOT_GIGABIT) {
            saveVerifiedAddress();
            const newRoute = getFullBuyRoute({
                filters: [ETariffFilters.SPEED100],
            });
            sendAnalytics('move_only_100', '/full-buy');
            router.push(newRoute);
        } else if (connectStatus === EConnectStatuses.CONNECT) {
            sendAnalytics('move_all_tariffs', '/bundles');
            router.push('/bundles');
        }
    };

    return (
        <ST.ResultForm>
            {(connectStatus !== EConnectStatuses.CONNECT) &&
                <ST.AlertWrap>
                    <Alert Icon={InfoCircle} type={'error'}>
                        <ST.AlertText dangerouslySetInnerHTML={{ __html: alertText[connectStatus] }}/>
                    </Alert>
                </ST.AlertWrap>
            }

            {!!texts[connectStatus] &&
                <ST.Text>{texts[connectStatus]}</ST.Text>
            }

            {connectStatus === EConnectStatuses.NOT_CONNECT &&
                <EditableContactsForm
                    onClickChangeAddress={onClickChangeAddress}
                />
            }

            {connectStatus !== EConnectStatuses.NOT_CONNECT &&
                <ST.BtnWrap>
                    <ST.Button
                        type={btnTypes.ACCENT}
                        text={additionalBtnText[connectStatus]}
                        onClick={onExecuteAction}
                    />
                    <ST.Button
                        type={btnTypes.SECONDARY}
                        text={'Проверить другой адрес'}
                        onClick={onClickChangeAddress}
                    />
                </ST.BtnWrap>
            }
        </ST.ResultForm>
    );
};
